import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { isNil } from 'ramda';
import { isNilOrEmptyString } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';

import { PKUrl } from 'components';
import { RoundedSquare } from 'components/atoms/CustomSVGIcon';
import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import MODALS from 'constants/modals';
import { changeSelectedFeedbackQuickType } from 'features/views/semaphore/actions';
import { contactAgeIds, unspecifiedFeedbackOption } from 'features/views/semaphore/constants';
import { equalOrThreeDashes, fromIsoDate, getCodelistByName, getCodelistPropById } from 'utils';
import { joinString } from 'utils/string';
import Feedback from 'pages/Semaphore/modals/Feedback';

export const SemaphoreTableRow = ({ record, isDetailVisible, onDetailVisibilityChange }) => {
	const {
		lastCommunicationAgeId,
		birthNumber,
		businessIn,
		zipCode,
		municipality,
		note,
		feedback,
		surname,
		firstName,
		partnerId,
		lastCommunication,
	} = record;

	const clientFullName = joinString(' ', [surname, firstName]);

	const semaphoreContactTypesCodelist = useSelector(getCodelistByName(codelists.semaphoreContactTypes));
	const originalFeedbackCodelist = useSelector(getCodelistByName(codelists.semaphoreFeedback));
	const feedbackCodelist = [...originalFeedbackCodelist, unspecifiedFeedbackOption];

	const handleToggleFeedback = useCallback(
		(e) => {
			e.stopPropagation();
			onDetailVisibilityChange();
		},
		[onDetailVisibilityChange],
	);

	const handleOpenFeedbackDetailModal = useCallback(() => {
		NiceModal.show(MODALS.semaphore_feedback_detail, { record });
	}, [record]);

	const generateInfoCircleTooltip = (feedbackDate, creationDate, note) => {
		let finalNote;
		// trim string, dont show whole note if longer than 100 chars
		const trimmedNote = isNil(note) ? EMPTY_VALUES.STRING : note.trim();
		if (trimmedNote.length > 100) {
			finalNote = trimmedNote.slice(0, 100).concat('...');
		} else {
			finalNote = trimmedNote;
		}

		const formattedFeedbackDate = equalOrThreeDashes(fromIsoDate(feedbackDate));
		const formattedCreationDate = equalOrThreeDashes(fromIsoDate(creationDate));

		const finalText = (
			<>
				<div>{`${getLabelMessage('semaphore:semaphoreFeedbackInputDate')}: ${formattedFeedbackDate}`}</div>
				<div>{`${getLabelMessage('semaphore:semaphoreNoteInputDate')}: ${formattedCreationDate}`}</div>
				<div>{finalNote}</div>
			</>
		);

		return <InfoCircleTooltip text={finalText} />;
	};

	const generateSquareTooltip = (lastCommunication, lastCommunicationAgeId) => {
		const formattedLastCommunicationDate = fromIsoDate(lastCommunication?.date);
		const communicationType = lastCommunication?.contactTypeId;
		const sellerFullName = joinString(' ', [lastCommunication.surname, lastCommunication.firstName]);
		const byWho = isNilOrEmptyString(sellerFullName)
			? equalOrThreeDashes(lastCommunication?.userName)
			: `${sellerFullName} (${equalOrThreeDashes(lastCommunication?.userName)})`;

		const result = {};
		switch (lastCommunicationAgeId) {
			case contactAgeIds.oneToTwoYears:
				result.className = 'OneToTwoYears';
				result.tooltip = getLabelMessage('semaphore:contact1To2year');
				break;
			case contactAgeIds.threeToFourYears:
				result.className = 'ThreeToFourYears';
				result.tooltip = getLabelMessage('semaphore:contact3To4year');
				break;
			case contactAgeIds.fiveToSixYears:
				result.className = 'FiveToSixYears';
				result.tooltip = getLabelMessage('semaphore:contact5To6year');
				break;
			case contactAgeIds.sevenAndLaterYears:
				result.className = 'SevenAndLaterYears';
				result.tooltip = getLabelMessage('semaphore:contact7AndLater');
				break;
		}

		const finalText = (
			<>
				<div>{result.tooltip}</div>
				<br />
				<div>{`${getLabelMessage('semaphore:semaphoreLastCommunicationWithClient')}:`}</div>
				<div>{`${getLabelMessage('semaphore:semaphoreLastCommunicationWhen')}: ${equalOrThreeDashes(
					formattedLastCommunicationDate,
				)}`}</div>
				<div>{`${getLabelMessage('semaphore:semaphoreLastCommunicationByWho')}: ${byWho}`}</div>
				<div>{`${getLabelMessage('semaphore:semaphoreLastCommunicationType')}: ${getCodelistPropById(
					communicationType,
					semaphoreContactTypesCodelist,
				)}`}</div>
			</>
		);

		return (
			<Tooltip title={finalText}>
				<IconButton disableRipple sx={{ fontSize: '15px' }}>
					<RoundedSquare className={result.className} />
				</IconButton>
			</Tooltip>
		);
	};

	return (
		<>
			<TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
				<TableCell align="center">{generateSquareTooltip(lastCommunication, lastCommunicationAgeId)}</TableCell>
				<TableCell>
					<PKUrl.PKPartner partnerId={partnerId} muiLink>
						{clientFullName}
					</PKUrl.PKPartner>
				</TableCell>
				<TableCell>
					<div>{birthNumber}</div>
					<div>{businessIn}</div>
				</TableCell>
				<TableCell>{zipCode}</TableCell>
				<TableCell>{municipality}</TableCell>
				<TableCell>{getCodelistPropById(feedback?.feedbackId, feedbackCodelist)}</TableCell>
				<TableCell>
					{!isNilOrEmptyString(note.text) &&
						generateInfoCircleTooltip(feedback?.date, note?.date, note?.text)}
				</TableCell>
				<TableCell>
					<Link
						sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 'bold' }}
						onClick={handleOpenFeedbackDetailModal}
						underline="none"
					>
						{getLabelMessage('semaphore:feedbackDetail')}
					</Link>
				</TableCell>
				<TableCell>
					{isDetailVisible ? (
						<ChevronUp
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					) : (
						<ChevronDown
							sx={{ color: 'primary.main', cursor: 'pointer' }}
							id="toggleIcon"
							onClick={handleToggleFeedback}
						/>
					)}
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={9}>
					<Collapse in={isDetailVisible} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							<TableRow id="semaphoreFeedback">
								<Feedback
									type="quickFeedback"
									record={record}
									onClickFn={changeSelectedFeedbackQuickType}
								/>
							</TableRow>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

SemaphoreTableRow.propTypes = {
	isDetailVisible: PropTypes.bool,
	onDetailVisibilityChange: PropTypes.func,
	record: PropTypes.object,
};
export default SemaphoreTableRow;
